import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { motion } from "framer-motion";

function renderMarkdown(md) {
    // tiny inline markdown renderer (#, ##, ###, **bold**, paragraphs)
    const blocks = md.split(/\n\n+/);
    return blocks.map((b, i) => {
        if (b.startsWith("### ")) return <h3 key={i} className="font-display text-2xl mt-8 mb-3">{b.slice(4)}</h3>;
        if (b.startsWith("## ")) return <h2 key={i} className="font-display text-3xl mt-10 mb-3">{b.slice(3)}</h2>;
        if (b.startsWith("# ")) return <h1 key={i} className="font-display text-4xl mt-10 mb-4">{b.slice(2)}</h1>;
        const parts = b.split(/(\*\*[^*]+\*\*)/g).map((p, j) => p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : p);
        return <p key={i} className="text-[#3a342e] leading-relaxed mt-4">{parts}</p>;
    });
}

export default function BlogPost() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        api.get(`/blog/${slug}`).then(r => {
            const result = r.data.post;
            if (!/event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|launch|promotion|gifting|celebration|venue|production|experience/i.test(`${result?.title} ${result?.category} ${(result?.tags || []).join(" ")}`)) {
                navigate("/blog", { replace: true });
                return;
            }
            setPost(result);
            setRelated((r.data.related || []).filter((item) => /event|wedding|corporate|exhibition|catering|farmhouse|mice|conference|launch|promotion|gifting|celebration|venue|production|experience/i.test(`${item.title} ${item.category} ${(item.tags || []).join(" ")}`)));
        }).catch(() => navigate("/blog", { replace: true }));
    }, [slug, navigate]);

    if (!post) return <div className="max-w-3xl mx-auto py-32 text-center">Loading…</div>;

    return (
        <div className="max-w-3xl mx-auto px-6 lg:px-10" data-testid="blog-post-page">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-mono-ui text-muted-foreground hover:text-[#121214]"><ArrowLeft className="h-3.5 w-3.5" /> Back to journal</Link>
            <p className="font-mono-ui text-[#857C72] mt-6">{post.category}</p>
            <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05]" data-testid="blog-post-title">{post.title}</h1>
            <div className="mt-5 flex items-center gap-5 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {post.author}</span>
                <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(post.created_at).toLocaleDateString()}</span>
            </div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8 relative aspect-[16/9] rounded-[28px] overflow-hidden border border-[#E7DED2] shadow-[0_24px_60px_rgba(18,18,20,0.18)]">
                <img src={post.cover_image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
            </motion.div>
            <article className="mt-10 prose-styled">
                {renderMarkdown(post.content)}
            </article>

            {related.length > 0 && (
                <section className="mt-20">
                    <h3 className="font-display text-3xl mb-6">Read next</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                        {related.map(r => (
                            <Link key={r.id} to={`/blog/${r.slug}`} className="group block">
                                <div className="relative aspect-[4/3] rounded-[20px] overflow-hidden border border-[#E7DED2]">
                                    <img src={r.cover_image} alt={r.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                </div>
                                <p className="font-display text-xl mt-3 group-hover:underline">{r.title}</p>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
            <div className="h-24" />
        </div>
    );
}
